import * as THREE from "three";
import { Sky } from "three/examples/jsm/objects/Sky.js";

/**
 * SkySun — physical sky dome (three Sky shader) with a real solar position for a given
 * geo location and time of day, plus a matching warm directional sun light.
 *
 * World convention (Tscheppa map space): +X = east, -Z = north, Y = up.
 * Time: local browser time by default; override via applyForHour(h) (e.g. ?sun=17.5).
 */
export class SkySun {
    private sky: Sky;
    private sunLight: THREE.DirectionalLight;
    private latDeg: number;
    private lngDeg: number;
    private maxLightIntensity: number;

    constructor(scene: THREE.Scene, latDeg: number, lngDeg: number, maxLightIntensity = 1.2) {
        this.latDeg = latDeg;
        this.lngDeg = lngDeg;
        this.maxLightIntensity = Math.max(0, maxLightIntensity);
        this.sky = new Sky();
        this.sky.scale.setScalar(100);
        const u = this.sky.material.uniforms;
        // Tuned for the engine's ACESFilmic @ exposure 1.0 (the three example uses 0.5 —
        // higher rayleigh/turbidity wash out to near-white there).
        u["turbidity"].value = 3.2;
        u["rayleigh"].value = 0.9;
        u["mieCoefficient"].value = 0.0025;
        u["mieDirectionalG"].value = 0.8;
        scene.add(this.sky);

        this.sunLight = new THREE.DirectionalLight(0xffffff, this.maxLightIntensity);
        scene.add(this.sunLight);
        scene.add(this.sunLight.target);
    }

    /** Solar elevation/azimuth (deg, azimuth from north, eastward) — NOAA-simplified. */
    private static solarPosition(date: Date, latDeg: number, lngDeg: number): { elevation: number; azimuth: number } {
        const rad = Math.PI / 180;
        const start = Date.UTC(date.getUTCFullYear(), 0, 0);
        const dayOfYear = (Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) - start) / 86400000;
        const decl = -23.44 * Math.cos(rad * (360 / 365) * (dayOfYear + 10));
        const utcHours = date.getUTCHours() + date.getUTCMinutes() / 60 + date.getUTCSeconds() / 3600;
        const solarTime = utcHours + lngDeg / 15; // mean solar time, good enough for visuals
        const hourAngle = (solarTime - 12) * 15;
        const lat = latDeg * rad, d = decl * rad, h = hourAngle * rad;
        const sinEl = Math.sin(lat) * Math.sin(d) + Math.cos(lat) * Math.cos(d) * Math.cos(h);
        const el = Math.asin(sinEl);
        const az = Math.atan2(Math.sin(h), Math.cos(h) * Math.sin(lat) - Math.tan(d) * Math.cos(lat));
        return { elevation: el / rad, azimuth: (az / rad + 180) % 360 }; // 0 = north, eastward
    }

    /** Apply the sun for the current date, optionally overriding the local hour (0-24). */
    public applyForHour(hourOverride?: number): void {
        const now = new Date();
        if (hourOverride !== undefined && isFinite(hourOverride)) {
            now.setHours(Math.floor(hourOverride), Math.round((hourOverride % 1) * 60), 0, 0);
        }
        const { elevation, azimuth } = SkySun.solarPosition(now, this.latDeg, this.lngDeg);
        // The Preetham-style shader is undefined below the horizon and renders muddy brown.
        // Twilight (real elevation -8..0): keep the SHADER sun pinned just above the horizon
        // for proper dusk colors. Deep night (< -8): hide the dome — the app's dark
        // star background takes over.
        if (elevation < -8) {
            this.sky.visible = false;
            this.sunLight.intensity = 0;
            return;
        }
        this.sky.visible = true;
        const shaderElevation = Math.max(elevation, 2);
        const rad = Math.PI / 180;
        const el = shaderElevation * rad, az = azimuth * rad;
        // world: +X east, -Z north
        const dir = new THREE.Vector3(
            Math.cos(el) * Math.sin(az),
            Math.sin(el),
            -Math.cos(el) * Math.cos(az)
        );
        this.sky.material.uniforms["sunPosition"].value.copy(dir);
        this.sunLight.position.copy(dir.clone().multiplyScalar(50));
        this.sunLight.target.position.set(0, 0, 0);
        // fade light out toward/below the horizon, warm it at low elevations
        const t = THREE.MathUtils.clamp(elevation / 60, 0, 1);
        this.sunLight.intensity = THREE.MathUtils.clamp(elevation / 8, 0, 1) * this.maxLightIntensity;
        this.sunLight.color.setHSL(0.12 - 0.04 * t, THREE.MathUtils.lerp(0.9, 0.25, t), THREE.MathUtils.lerp(0.55, 0.99, t));
    }
}
